import React from 'react';
import { connect } from 'react-redux';
import { OrderedMap } from 'immutable';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { debounce, isEmpty, assign } from 'lodash';

import { CHAR_LIMIT } from 'chat/constants/MessageComposerConstants';
import { flush, saveMessage } from 'reducers/roleBasedMessagingSearch';
import { getInitialAnnouncementTextForGroup } from 'lib/getInitialAnnouncementTextForGroup';
import { Message, TARGET_TYPES } from 'entities/Message';
import { ComposeMessageRecipientsInput } from 'chat/components/SplitComposer';
import { uploadFile as uploadFileHelper } from 'lib/fileUploadHelper';
import AccountLinkingComposerSidebar from 'chat/components/AccountLinkingComposerSidebar';
import CurrentUser from 'auth/CurrentUser';
import DropZoneModal from 'components/DropZoneModal';
import EventTracking from 'lib/EventTracking';
import LayerTrigger from 'components/LayerTrigger';
import linkPreviewQuery from 'chat/queries/linkPreview.gql';
import MediaObject from 'components/MediaObject';
import RemindAPI from 'lib/RemindAPI';
import Row from 'components/Row';
import TextEntities from 'lib/TextEntities';
import TextWithGlyph from 'components/TextWithGlyph';
import Tooltip from 'components/Tooltip';
import uuid from 'uuid/v4';
import Image from 'components/Image';
import VisualComposer from './ShareSDKVisualComposer';
import ComposerFilePreview from 'chat/components/ComposerFilePreview';
import { uploadFileRequest, fetchJSONFileByIdRequest } from 'clients/api/fileUploads';
import { PreviewType } from 'chat/components/_shared/types/PreviewType';
import Carousel from 'chat/components/_shared/Carousel';
import AttachMore from 'chat/components/_shared/Carousel/AttachMore';
import { LinkPreviewType } from 'chat/components/_shared/types/LinkPreviewType';
import TooManyAttachmentsDialog from 'chat/components/_shared/TooManyAttachmentsDialog';

import type { V2File } from 'clients/api/fileUploads';
import type { Target } from 'entities/Message';
import type { UIEventContext } from './ShareSDKComposerContext';
import type { LinkPreview_linkPreview } from 'chat/queries/__generated__/LinkPreview';
import type { ContentSourcesContent_me_composer_contentSourcesContent_items_downloadPreview as DownloadPreview } from 'user/queries/__generated__/ContentSourcesContent';

import type { ReactElement } from 'react';

export type Props = {
  onRequestHide: () => void;
  initialTargets?: Target[];
  draftAnnouncement: Message | undefined | null;
  isEditingScheduledMessage: boolean;
  onBack: Function;
  onMessageSent?: Function;
  isUrgentMessage?: boolean;
  // Redux state
  targets: Target[];
  districtIsSelected: boolean;
  // @ts-expect-error
  district: ComposerOrganization | undefined | null;
  savedMessage: string;
  // Redux dispatchers
  onFlush: () => any;
  saveMessage: (message: string) => any;
  // Drag 'n drop props
  connectDropTarget: Function;
  isDraggingFile?: boolean;
  // UIEventContext from composerContext
  uiEventContext: UIEventContext;
  // sharesdk url from url params
  shareSdkBodyUrl?: string;
};

type State = {
  multiAttachmentLimit: number;
  text: string;
  scheduledTime: Date | undefined | null;
  uuidForNewMessage: string | undefined | null;
  linkPreviews: OrderedMap<string, any>;
  file?: V2File;
  filePreviews: PreviewType[];
  fileData?: File;
  thumbnail?: string;
  uploadProgress?: number;
  uploadError?: Error;
  showMaxAttachmentsDialog: Boolean;
};

export const URGENT_STRING = 'URGENT: ';

export class ComposeMessage extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state: State;
  _textInput: HTMLInputElement | undefined = undefined;

  constructor(props: Props) {
    super(props);
    let multiAttachmentLimit = 1;
    if (CurrentUser.isSignedIn()) {
      const currentUser = CurrentUser.getUser();
      multiAttachmentLimit =
        currentUser.attributes?.settings?.messages?.files?.multi_attachment_limit ?? 1;
    }

    this.state = {
      multiAttachmentLimit,
      text: '',
      scheduledTime: null as Date | undefined | null,
      uuidForNewMessage: uuid(),
      // @ts-expect-error
      linkPreviews: new OrderedMap(),
      filePreviews: [],
      showMaxAttachmentsDialog: false,
    };

    const { draftAnnouncement } = props;
    if (draftAnnouncement) {
      Object.assign(this.state, {
        scheduledTime: draftAnnouncement.getSendAt(),
      });

      const body = draftAnnouncement.getBody();
      if (body) {
        Object.assign(this.state, {
          text: body,
        });
      }
      // override the random uuid with one of the initial message, if it has one
      if (draftAnnouncement.getUuid()) {
        Object.assign(this.state, { uuidForNewMessage: draftAnnouncement.getUuid() });
      }
      const linkPreviews = {};
      draftAnnouncement.getLinkPreviews().forEach((p) => {
        // @ts-expect-error
        linkPreviews[p.getUrl()] = {
          title: p.getTitle(),
          description: p.getDescription(),
          url: p.getUrl(),
          image: p.getImageUrl(),
          image_width: p.getImageWidth(),
          image_height: p.getImageHeight(),
          rich_content_type: p.getRichContentType(),
          rich_content_url: p.getRichContentUrl(),
          addedAtUnix: Date.now(),
        };
      });
      Object.assign(this.state, { linkPreviews: OrderedMap(linkPreviews) });
    }

    if (props.shareSdkBodyUrl) {
      this.getLinkPreviews([props.shareSdkBodyUrl])
        .then(() => (props.shareSdkBodyUrl = undefined))
        .catch(() => {
          /* do nothing */
        });
    }
  }

  async componentWillMount() {
    const { groupCode } = this.context.router.params;
    const { draftAnnouncement, initialTargets, savedMessage, isUrgentMessage } = this.props;

    if (!draftAnnouncement && initialTargets && !savedMessage.length) {
      const initialTarget = initialTargets.length ? initialTargets[0] : null;
      const initialText =
        groupCode && initialTarget
          ? await getInitialAnnouncementTextForGroup(groupCode, initialTarget)
          : '';
      // urgent messages start with 'Urgent: ' - see URGENT_STRING
      this.setState({ text: isUrgentMessage ? `${URGENT_STRING}${initialText}` : initialText });
    } else if (savedMessage.length) {
      this.setState({ text: savedMessage });
    } else if (isUrgentMessage && !draftAnnouncement) {
      this.setState({
        text: URGENT_STRING,
      });
    }
  }

  async componentDidMount() {
    const { draftAnnouncement } = this.props;
    const { text } = this.state;

    if (text) {
      this.props.saveMessage(text);
    }

    if (draftAnnouncement) {
      const fileId = draftAnnouncement.getAttachmentId() || draftAnnouncement.getFile()?.id;
      if (fileId) {
        const uploadedFile = await fetchJSONFileByIdRequest(fileId);
        this.setState({ file: uploadedFile, uploadProgress: 100 });
      }
    }

    EventTracking.trackKey('announcements.composer.view', {
      composer_version: 'share_sdk',
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.text !== this.state.text) {
      this.props.saveMessage(this.state.text);
    }
  }

  clearReduxState = () => {
    this.resetFileState();
    this.props.onFlush();
  };

  debouncedGetLinkPreviews = debounce((urls: Array<string>) => this.getLinkPreviews(urls), 500);

  getLinkPreviews = (urls: string[]): Promise<void[]> =>
    Promise.all(urls.map((u: string): Promise<void> => this.getSingleLinkPreview(u, null, null)));

  getSingleLinkPreview = async (
    url: string,
    existingPreview?: Partial<LinkPreview_linkPreview> | null,
    downloadPreview?: DownloadPreview | null
  ): Promise<void> => {
    try {
      const data = await RemindAPI.execute({
        query: linkPreviewQuery,
        variables: { ...(existingPreview || {}), url },
      });

      // @ts-expect-error
      const { linkPreview } = data;
      if (linkPreview) {
        // this is to resolve a bug involving the link preview service returning the _entire_ pdf
        // will also resolve this on the service as well.
        const description = linkPreview.description && linkPreview.description.slice(0, 1024);

        const { title, imageUrl, imageWidth, imageHeight, richContentType, richContentUrl } =
          linkPreview;

        const { text, linkPreviews } = this.state;
        const linksInText = TextEntities.parseUrls(text).map((entity) =>
          text.substring(entity.offset, entity.offset + entity.length)
        );

        let newLinkPreviews = linkPreviews;
        if (linksInText.includes(url)) {
          newLinkPreviews = newLinkPreviews.set(url, {
            title,
            description,
            url,
            image: imageUrl,
            image_width: imageWidth ? String(imageWidth) : imageWidth,
            image_height: imageHeight ? String(imageHeight) : imageHeight,
            rich_content_type: richContentType,
            rich_content_url: richContentUrl,
            downloadPreview,
            addedAtUnix: Date.now(),
          });
        }

        this.setState({
          linkPreviews: newLinkPreviews,
        });
      }
    } catch (error) {
      console.error('could not get link preview', error);
    }
  };

  changeText = (text: string) => {
    const { linkPreviews } = this.state;

    // Get previews for links added to text
    const linksInText = TextEntities.parseUrls(text).map((entity) =>
      text.substring(entity.offset, entity.offset + entity.length)
    );
    const newLinks = linksInText.filter((url) => !linkPreviews.has(url));

    this.debouncedGetLinkPreviews(newLinks);

    // Remove previews for links that are removed from text
    const currentLinkedPreviews = linkPreviews.filter((content) =>
      linksInText.includes(content.url)
    );

    this.props.saveMessage(text);

    this.setState({
      text,
      linkPreviews: currentLinkedPreviews,
    });
  };

  changeScheduledTime = (scheduledTime?: Date | null) => {
    this.setState({ scheduledTime });
  };

  handleComposerSent = () => {
    const { targets, onMessageSent } = this.props;
    const { scheduledTime } = this.state;

    if (onMessageSent) {
      onMessageSent({ scheduledTime, targets });
    }

    // Get a new uuid in case another message is composed
    this.setState({ uuidForNewMessage: uuid() });
    this.clearReduxState();
  };

  renderDropzoneModal = (): ReactElement<any> | undefined | null => {
    const { isDraggingFile } = this.props;
    const { fileData } = this.state;

    if (!isDraggingFile) {
      return null;
    }

    return (
      <div>
        <DropZoneModal titleDrop="Drop a file here" isFileUploaded={!!fileData} isHovering />
      </div>
    );
  };

  addLinkedContent = (content: any) => {
    // This method adds content from the content browser.
    const { text, linkPreviews } = this.state;
    const { title, description, contentUrl, imageUrl, downloadPreview } = content;

    if (linkPreviews.has(contentUrl)) {
      return;
    }

    // Only add a space in front of the link if there's already text there
    const hasText = text.trim().length > 0;
    const conditionalSpace = hasText ? ' ' : '';
    this.setState(
      {
        text: `${text}${conditionalSpace}${contentUrl} `,
        // Set an empty object to stop the same content from being added
        // multiple times while the preview resolves
        linkPreviews: linkPreviews.set(contentUrl, {}),
      },
      () => {
        this.getSingleLinkPreview(
          contentUrl,
          {
            url: contentUrl,
            title,
            description,
            imageUrl,
          },
          downloadPreview
        );
      }
    );
  };

  removeLinkPreview = (url: string) => {
    const { text, linkPreviews } = this.state;

    this.setState({
      // Try to remove the url with the default whitespace first
      text: text
        .replace(url + ' ', '')
        .replace(url, '')
        .trim(),
      linkPreviews: linkPreviews.remove(url),
    });
  };

  targetsContainGroupsOrOrgs = () => {
    const { targets } = this.props;
    if (targets) {
      for (const target of targets) {
        if (
          target.type === TARGET_TYPES.GROUP_TARGET ||
          target.type === TARGET_TYPES.ORGANIZATION_TARGET
        ) {
          return true;
        }
      }
    }
    return false;
  };

  characterLimit = () => {
    return this.targetsContainGroupsOrOrgs()
      ? CurrentUser.getUser().getAnnouncementCharactersLimit()
      : CHAR_LIMIT;
  };

  characterLimitWithFiles = () => {
    return this.targetsContainGroupsOrOrgs()
      ? CurrentUser.getUser().getAnnouncementCharactersLimitWithFiles()
      : CHAR_LIMIT;
  };

  handleUploadFromInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      Array.from(event.target.files).forEach((file) => {
        this.uploadFile(file);
      });
    }
  };

  uploadFile = (fileData: File) => {
    const { multiAttachmentLimit } = this.state;
    let underTheUploadLimit = multiAttachmentLimit - this.state.filePreviews.length > 0;
    if (!underTheUploadLimit) {
      this.setState({ showMaxAttachmentsDialog: true });
      return;
    }

    this.setState({ fileData });

    let tempFilePreview: PreviewType = {
      filePreview: {
        id: undefined,
        error: false,
        progress: 0,
      },
      addedAtUnix: Date.now(),
    };

    let filePreviews = this.state.filePreviews || [];
    filePreviews.push(tempFilePreview);

    uploadFileRequest(fileData, false, (percent: number) => {
      if (this.state.fileData) {
        assign(
          {
            filePreview: {
              progress: percent,
              error: false,
            },
          },
          tempFilePreview
        );
        this.setState({ uploadProgress: percent, filePreviews: filePreviews });
      }
    })
      .then((response) => {
        if (this.state.fileData) {
          assign(tempFilePreview, {
            filePreview: {
              id: response.id,
              error: false,
              progress: 100,
              thumbnail: Image.isSupportedType(response.content_type)
                ? response.urls.cdn
                : undefined,
              fileName: response.name,
              type: response.content_type,
              url: response?.urls?.cdn || undefined,
            },
          });
          this.setState({ file: response, filePreviews: filePreviews });
        }
      })
      .catch((err) => this.setState({ uploadError: err }));
  };

  deletePreview = (index: number) => {
    const previews = this.getGatheredPreviews();
    const previewToDelete = previews[index];
    const isFilePreview = !!previewToDelete.filePreview;

    if (isFilePreview) {
      this.setState({
        filePreviews: this.state.filePreviews.filter(
          (preview) => preview.filePreview?.fileName !== previewToDelete.filePreview.fileName
        ),
      });
    } else {
      this.removeLinkPreview(previewToDelete.linkPreview.url);
    }
  };

  openUploadDialog = () => {
    this._textInput?.click();
  };

  getFileUuids = (filePreviews: PreviewType[]): string[] => {
    return filePreviews?.reduce<string[]>((accum, filePreview) => {
      if (filePreview?.filePreview?.id) {
        accum.push(filePreview.filePreview.id);
      }
      return accum;
    }, []);
  };

  getGatheredPreviews = () => {
    const { linkPreviews, filePreviews } = this.state;
    const jsLinkPreviews = linkPreviews.toArray().map((item) => item[1]) as LinkPreviewType[];
    const linkPreviewsToAccordion: PreviewType[] = jsLinkPreviews.map((linkPreview) => ({
      linkPreview,
      addedAtUnix: linkPreview.addedAtUnix,
    }));
    const previews = [...filePreviews, ...linkPreviewsToAccordion].sort((a, b) =>
      (a.addedAtUnix ?? 0) > (b.addedAtUnix ?? 0) ? 1 : -1
    );
    return previews;
  };

  getFilePreviewer = (multiAttachmentLimit: number) => {
    const { file, fileData, thumbnail, uploadProgress, uploadError, filePreviews } = this.state;

    const previews = this.getGatheredPreviews();

    let filePreview = null;
    if (multiAttachmentLimit > 1) {
      filePreview =
        previews.length > 0 ? (
          <Carousel
            extraItem={
              <AttachMore
                disabled={filePreviews.length >= multiAttachmentLimit}
                maxAttachments={multiAttachmentLimit}
                onClick={this.openUploadDialog}
              />
            }
            onDeletePreview={this.deletePreview}
            previews={previews}
          />
        ) : null;
    } else if (!!file || !!fileData) {
      filePreview = (
        <ComposerFilePreview
          progress={uploadProgress}
          thumbnail={thumbnail}
          type={file?.content_type}
          error={!!uploadError}
          onDelete={() => {
            this.resetFileState();
          }}
          onRetry={() => {
            if (fileData) {
              this.uploadFile(fileData);
            }
          }}
        />
      );
    }
    return filePreview;
  };

  resetFileState = () => {
    this.setState({
      file: undefined,
      fileData: undefined,
      thumbnail: undefined,
      uploadError: undefined,
      uploadProgress: undefined,
    });
  };

  renderSenderInfoText = (
    // @ts-expect-error
    district: ComposerOrganization | undefined | null,
    uiEventContext: UIEventContext
  ) => {
    if (district) {
      const districtUuid = district.uuid;
      return (
        <Row size="xsmall">
          <MediaObject figure="right">
            <div />
            <LayerTrigger
              layer={
                <Tooltip>Recipients aren’t able to reply to messages sent from a district</Tooltip>
              }
              placement="top"
              alignment="middle"
              onShow={() =>
                EventTracking.trackKey('announcements.composer.sending_as.click', {
                  ...uiEventContext,
                  organization_uuid: districtUuid,
                })
              }
            >
              <span>
                <TextWithGlyph
                  color="graphite"
                  size="xxsmall"
                  glyph="help"
                  glyphColor="graphite"
                  glyphSize="small"
                >
                  Sending as {district.name}
                </TextWithGlyph>
              </span>
            </LayerTrigger>
          </MediaObject>
        </Row>
      );
    }
    return null;
  };

  render() {
    const {
      targets,
      connectDropTarget,
      initialTargets,
      draftAnnouncement,
      districtIsSelected,
      district,
      isEditingScheduledMessage,
      isUrgentMessage,
      uiEventContext,
    } = this.props;

    const {
      text,
      scheduledTime,
      uuidForNewMessage,
      linkPreviews,
      file,
      filePreviews,
      multiAttachmentLimit,
      showMaxAttachmentsDialog,
    } = this.state;
    const autoFocusComposer = initialTargets != null;
    const underTheMultiAttachmentLimit =
      this.state.filePreviews.length < this.state.multiAttachmentLimit;

    const sidebar = (
      <AccountLinkingComposerSidebar
        disabledUploads={!underTheMultiAttachmentLimit}
        addLinkedContent={this.addLinkedContent}
        uploadRequest={(fileContextKey: string, fileKey: string, data: File) =>
          this.uploadFile(data)
        }
        loadThumbnail={(fileContextKey: string, filename: string, newThumbnail: string) => {
          this.setState({ thumbnail: newThumbnail });
        }}
      />
    );

    return connectDropTarget(
      <div>
        {this.renderDropzoneModal()}
        {showMaxAttachmentsDialog && (
          <TooManyAttachmentsDialog
            onHideButtonClicked={() => this.setState({ showMaxAttachmentsDialog: false })}
            max={multiAttachmentLimit}
          />
        )}
        <div className="ComposeMessage">
          <div className="ComposeMessage-inner">
            <div className="ComposeMessage-composer">
              {districtIsSelected && this.renderSenderInfoText(district, uiEventContext)}
              {/* @ts-expect-error */}
              <ComposeMessageRecipientsInput onClick={this.props.onBack} />
              <VisualComposer
                autoFocus={autoFocusComposer}
                // @ts-expect-error
                initialMessage={draftAnnouncement}
                className="ComposeMessage-composer-container"
                onMessageSent={this.handleComposerSent}
                charLimit={this.characterLimit()}
                charLimitWithFiles={this.characterLimitWithFiles()}
                districtIsSelected={districtIsSelected}
                targets={targets}
                isEditingScheduledMessage={isEditingScheduledMessage}
                onTextChange={this.changeText}
                // @ts-expect-error
                uuidForNewMessage={uuidForNewMessage}
                onScheduledTimeChange={this.changeScheduledTime}
                // @ts-expect-error
                scheduledTime={scheduledTime}
                text={text}
                linkPreviews={linkPreviews.filter((v) => !isEmpty(v))}
                onRequestHide={this.props.onRequestHide}
                orgUuid={district ? district.uuid : null}
                orgName={district ? district.name : null}
                // @ts-expect-error
                onBack={this.props.onBack}
                isUrgentMessage={isUrgentMessage}
                uploadRequest={(fileContextKey: string, fileKey: string, data: File) =>
                  this.uploadFile(data)
                }
                loadThumbnail={(fileContextKey: string, filename: string, newThumbnail: string) => {
                  this.setState({ thumbnail: newThumbnail });
                }}
                fileUuid={file?.id}
                fileUuids={this.getFileUuids(filePreviews)}
                filePreview={this.getFilePreviewer(multiAttachmentLimit)}
              />
              <input
                ref={(input) => {
                  this._textInput = input || undefined;
                }}
                data-testid={'upload-input'}
                type="file"
                onChange={this.handleUploadFromInput}
                style={{ display: 'none' }}
                multiple={multiAttachmentLimit > 1}
              />
            </div>
            <div className="ComposeMessage-sidebar">{sidebar}</div>
          </div>
        </div>
      </div>
    );
  }
}

const dndEvents = {
  // @ts-expect-error
  drop: (props, monitor, component) => {
    let multiAttachmentLimit = 1;
    if (CurrentUser.isSignedIn()) {
      const currentUser = CurrentUser.getUser();
      multiAttachmentLimit =
        currentUser.attributes?.settings?.messages?.files?.multi_attachment_limit ?? 1;
    }
    if (multiAttachmentLimit > 1) {
      monitor.getItem().files.forEach((fileData: File) => {
        uploadFileHelper(
          '',
          fileData,
          (fileContextKey: string, fileKey: string, data: File) => component.uploadFile(data),
          (fileContextKey: string, filename: string, newThumbnail: string) =>
            component.setState({ thumbnail: newThumbnail })
        );
      });
    } else {
      uploadFileHelper(
        '',
        monitor.getItem().files[0],
        (fileContextKey: string, fileKey: string, data: File) => component.uploadFile(data),
        (fileContextKey: string, filename: string, newThumbnail: string) =>
          component.setState({ thumbnail: newThumbnail })
      );
    }

    EventTracking.trackKey(
      'announcements.announcement_modal.universal_composer.create_attachment.drag_and_drop',
      props.uiEventContext
    );
  },
};

// @ts-expect-error
const collect = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const withDropTarget = DropTarget(NativeTypes.FILE, dndEvents, collect);

const mapDispatchToProps = (dispatch: Function) =>
  bindActionCreators(
    {
      onFlush: flush,
      saveMessage,
    },
    // @ts-expect-error
    dispatch
  );

const mapStateToProps = (state: any) => {
  const { roleBasedMessagingSearch } = state;
  return {
    targets: roleBasedMessagingSearch.targets,
    savedMessage: roleBasedMessagingSearch.message,
    district: roleBasedMessagingSearch.districtIsSelected
      ? roleBasedMessagingSearch.selection[0].organization
      : null,
    districtIsSelected: roleBasedMessagingSearch.districtIsSelected,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withDropTarget(ComposeMessage));
